import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../constants/api";
import { showError } from "../utils/toast.js";
import ListContainer from "../components/ui/ListContainer.jsx";
import FilterBarOrDrawer from "../components/ui/FilterBarOrDrawer.jsx";
import EntityCardBlock from "../components/shared/EntityCardBlock.jsx";
import StatusFilter from "../components/ui/StatusFilter.jsx";
import SortButton from "../components/ui/SortButton.jsx";
import { smartBack } from "../constants/nav";
import AppButton from "../components/ui/AppButton.jsx";
import { Input } from "../components/forms/shared/FormAtoms.jsx";
import { NeutralBadge } from "../components/ui/TypBadge.jsx";
import { detailPageHeaderNavClassName, detailPageHeaderNavTouchClassName, detailPageHeaderNavActionsClassName, detailPageHeaderNavPrimaryMobileClassName } from "../design/tokens.js";
const TYPEN = ["IT", "ALARM", "VIDEO", "ELEKTRO", "PV", "LADESTATION"];
const STATUS = ["ENTWURF", "AKTIV", "PAUSIERT", "GEKUENDIGT", "ABGELAUFEN", "ARCHIVIERT"];
export default function WartungsvertraegePage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    typ: "__all__",
    status: "__all__",
    q: "",
  });
  const [sort, setSort] = useState({
    key: "titel",
    dir: "asc",
  });
  const sortOptions = useMemo(
    () => [
      { key: "titel", label: "Titel", section: "Allgemein" },
      { key: "kunde", label: "Kunde (Firma/Name)", section: "Allgemein" },
      { key: "faelligkeit", label: "Fälligkeit", section: "Allgemein" },
      { key: "neueste", label: "Neueste zuerst", section: "Zeit", fixedDir: "desc" },
      { key: "aelteste", label: "Älteste zuerst", section: "Zeit", fixedDir: "asc" },
    ],
    [],
  );
  const defaultDirFor = useCallback((key) => (key === "neueste" ? "desc" : "asc"), []);
  const handleSortChange = useCallback((next) => setSort(next), []);

  async function load() {
    try {
      setLoading(true);
      const params = {
        q: filters.q || undefined,
        typ: filters.typ !== "__all__" ? filters.typ : undefined,
        status: filters.status !== "__all__" ? filters.status : undefined,
      };
      const { data } = await api.get("/wartungsvertraege", { params });
      setRows(Array.isArray(data?.data) ? data.data : []);
    } catch {
      showError("Wartungsverträge konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [filters.typ, filters.status]);

  const filteredRows = useMemo(() => {
    const q = String(filters.q || "").trim().toLowerCase();
    let list = rows;
    if (filters.typ !== "__all__") list = list.filter((r) => String(r.typ || "").toUpperCase() === filters.typ);
    if (filters.status !== "__all__") {
      list = list.filter((r) => String(r.status || "").toUpperCase() === filters.status);
    } else {
      list = list.filter((r) => String(r.status || "").toUpperCase() !== "ARCHIVIERT");
    }
    if (!q) return list;
    const needle = q.toLowerCase();
    const fields = (row) => [
      row.id,
      row.typ,
      row.status,
      row.titel,
      row.kunde_label,
      row.objekt_label,
      row.kunden_firma,
      row.kunden_vorname,
      row.kunden_nachname,
      row.kunden_strasse,
      row.kunden_plz,
      row.kunden_ort,
      row.verwaltung_text,
      row.verantwortlich_name,
    ];
    return list.filter((r) => fields(r).some((x) => String(x || "").toLowerCase().includes(needle)));
  }, [rows, filters]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    const dirMul = sort.dir === "asc" ? 1 : -1;
    const n = (v) => String(v || "").trim().toLowerCase();
    const d = (row) => {
      const raw = row?.next_due_at || row?.vertragsbeginn || row?.created_at;
      const dt = raw ? new Date(String(raw).slice(0, 10)) : null;
      return dt && !Number.isNaN(dt.getTime()) ? dt.getTime() : null;
    };
    arr.sort((a, b) => {
      if (sort.key === "neueste") return Number(b.id || 0) - Number(a.id || 0);
      if (sort.key === "aelteste") return Number(a.id || 0) - Number(b.id || 0);
      if (sort.key === "kunde") {
        const cmp = n(a.kunde_label).localeCompare(n(b.kunde_label));
        return cmp !== 0 ? cmp * dirMul : (Number(a.id || 0) - Number(b.id || 0)) * dirMul;
      }
      if (sort.key === "faelligkeit") {
        const da = d(a);
        const db = d(b);
        if (da == null && db == null) return 0;
        if (da == null) return 1;
        if (db == null) return -1;
        if (da === db) return 0;
        return da < db ? -1 * dirMul : 1 * dirMul;
      }
      const cmp = n(a.titel || `Wartungsvertrag #${a.id}`).localeCompare(n(b.titel || `Wartungsvertrag #${b.id}`));
      return cmp !== 0 ? cmp * dirMul : (Number(a.id || 0) - Number(b.id || 0)) * dirMul;
    });
    return arr;
  }, [filteredRows, sort]);

  const renderWartungCard = useCallback(
    (item) => (
      <EntityCardBlock
        entity={item}
        entityType="wartung"
        metaRight={
          item?.typ ? (
            <NeutralBadge
              small
              className="shrink-0"
              text={String(item.typ).trim().toUpperCase() || "—"}
              title={`Vertragstyp: ${String(item.typ).trim().toUpperCase()}`}
            />
          ) : null
        }
      />
    ),
    [],
  );

  const grad = "min-h-screen w-full bg-gradient-to-br from-[#05070c] via-[#0a1020] to-[#0a1a32] text-white";
  const glass = "app-frame";
  const topbarCls = `rounded-2xl px-4 py-3 mb-6 border ${glass}`;
  const searchCardCls = `rounded-xl px-2.5 py-2 border mb-6 ${glass}`;
  const qTrim = String(filters.q || "").trim();

  const header = (
    <>
      <div className={topbarCls}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight">🧰 Wartungsverträge</h1>
            <p className="mt-1 text-sm text-blue-200/80">{!loading ? `${sortedRows.length} Verträge` : "Lade Daten…"}</p>
          </div>
          <div className={detailPageHeaderNavActionsClassName}>
            <AppButton type="button" variant="primary" size="lg" className={`${detailPageHeaderNavTouchClassName} ${detailPageHeaderNavPrimaryMobileClassName}`} onClick={() => navigate("/wartungsvertraege/neu")}>
              <span className="md:hidden">Neu</span>
              <span className="hidden md:inline">Neuer Wartungsvertrag</span>
            </AppButton>
            <AppButton
              type="button"
              variant="secondary"
              size="lg"
              className={detailPageHeaderNavClassName}
              title="Zu Meine Aufgaben"
              onClick={() => navigate("/meine-aufgaben")}
            >
              Meine Aufgaben
            </AppButton>
            <AppButton type="button" variant="secondary" size="lg" className={detailPageHeaderNavClassName} onClick={() => smartBack(navigate, ["/", "/dashboard"])}>
              Zurück
            </AppButton>
            <AppButton type="button" variant="secondary" size="lg" className={detailPageHeaderNavClassName} title="Zum Dashboard" onClick={() => navigate("/")}>
              Dashboard
            </AppButton>
          </div>
        </div>
      </div>

      <div className={searchCardCls}>
        <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:gap-2">
          <div className="relative w-full min-w-0 xl:flex-1 xl:min-w-[20rem]">
            <Input
              type="search"
              inputMode="search"
              autoComplete="off"
              enterKeyHint="search"
              aria-label="Wartungsverträge durchsuchen"
              role="searchbox"
              placeholder="Suche…"
              title="Titel, Kunde, Adresse/Ort, Status, Typ, Objekt …"
              value={filters.q}
              onChange={(e) =>
                setFilters((s) => ({
                  ...s,
                  q: e.target.value,
                }))
              }
              className={`px-3 py-2 min-h-[40px] pr-10 text-[16px] xl:text-sm [appearance:textfield] [&::-webkit-search-cancel-button]:hidden`}
            />
            {qTrim ? (
              <AppButton
                type="button"
                variant="ghost"
                onClick={() => setFilters((s) => ({ ...s, q: "" }))}
                className="absolute right-1 top-1/2 -translate-y-1/2 !min-h-[36px] !min-w-[36px] !p-0 !border-0 !bg-transparent !shadow-none text-blue-200/70 hover:!bg-transparent hover:text-white"
                aria-label="Suche löschen"
                title="Suche löschen"
              >
                ✕
              </AppButton>
            ) : null}
          </div>

          <div className="hidden xl:flex xl:flex-nowrap xl:items-center xl:gap-2 xl:min-w-0 xl:ml-auto xl:justify-end xl:overflow-x-auto">
              <div className="flex flex-wrap items-center gap-1 min-w-0">
                {[{ value: "__all__", label: "Alle" }, ...TYPEN.map((t) => ({ value: t, label: t }))].map((opt) => (
                  <AppButton
                    key={opt.value}
                    type="button"
                    onClick={() =>
                      setFilters((s) => ({
                        ...s,
                        typ: s.typ === opt.value ? "__all__" : opt.value,
                      }))
                    }
                    variant="ghost"
                    size="sm"
                    className={`min-h-[36px] ${filters.typ === opt.value ? "seg-btn-active-primary font-semibold" : "seg-btn-idle"}`}
                  >
                    {opt.label}
                  </AppButton>
                ))}
              </div>
              <StatusFilter
                value={filters.status}
                onChange={(v) =>
                  setFilters((s) => ({
                    ...s,
                    status: v,
                  }))
                }
                labelAll="Alle Status"
                optionsOverride={STATUS}
              />
              <SortButton
                value={sort}
                onChange={handleSortChange}
                options={sortOptions}
                defaultDirFor={defaultDirFor}
                allowDirToggle={(key) => !["neueste", "aelteste"].includes(String(key || "").toLowerCase())}
              />
            </div>

          <div className="xl:hidden w-full min-w-0">
            <FilterBarOrDrawer title="Filter & Sortierung" drawerUntil="xl" className="w-full justify-center">
              <div className="flex flex-col gap-2 min-w-0">
                <StatusFilter
                  value={filters.typ}
                  onChange={(v) =>
                    setFilters((s) => ({
                      ...s,
                      typ: v,
                    }))
                  }
                  labelAll="Alle Typen"
                  optionsOverride={TYPEN}
                  triggerClassName="w-full justify-center text-sm min-h-[40px]"
                />
                <StatusFilter
                  value={filters.status}
                  onChange={(v) =>
                    setFilters((s) => ({
                      ...s,
                      status: v,
                    }))
                  }
                  labelAll="Alle Status"
                  optionsOverride={STATUS}
                  triggerClassName="w-full justify-center text-sm min-h-[40px]"
                />
                <SortButton
                  value={sort}
                  onChange={handleSortChange}
                  options={sortOptions}
                  defaultDirFor={defaultDirFor}
                  allowDirToggle={(key) => !["neueste", "aelteste"].includes(String(key || "").toLowerCase())}
                  className="w-full justify-center text-sm min-h-[40px]"
                />
              </div>
            </FilterBarOrDrawer>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <main className={grad}>
      <div className="mx-auto max-w-6xl px-4 py-6">
        <ListContainer
          header={header}
          items={sortedRows}
          loading={loading}
          emptyMessage="Keine Wartungsverträge gefunden."
          gridCols="grid-cols-1 md:grid-cols-2"
          renderItem={renderWartungCard}
        />
      </div>
    </main>
  );
}
